export type PageAccessConfig = Record<string, string[]>;

// Fallback config used when backend is unreachable and no cache exists
const DEFAULT_CONFIG: PageAccessConfig = {
  'place-order':             ['ADMIN', 'MANAGER', 'CASHIER', 'WAITER'],
  'dashboard':               ['ADMIN', 'MANAGER', 'CASHIER', 'WAITER', 'CHEF', 'KITCHEN_STAFF', 'RECEPTIONIST'],
  'orders':                  ['ADMIN', 'MANAGER', 'CASHIER'],
  'tables':                  ['ADMIN', 'MANAGER', 'CASHIER', 'WAITER'],
  'customers':               ['ADMIN', 'MANAGER', 'CASHIER'],
  'items':                   ['ADMIN', 'MANAGER', 'CHEF'],
  'kitchens':                ['ADMIN', 'MANAGER'],
  'reports':                 ['ADMIN', 'MANAGER'],
  'profile':                 ['ADMIN', 'MANAGER', 'CASHIER', 'WAITER', 'CHEF', 'KITCHEN_STAFF', 'RECEPTIONIST'],
  'settings':                ['ADMIN'],
  'organization-onboarding': ['ADMIN'],
  'employee-management':     ['ADMIN', 'MANAGER'],
  'role-page-config':        ['ADMIN'],
};

const STORAGE_KEY = 'restro360_role_page_config';
const ACTIVE_RESTAURANT_KEY = 'restro360_active_restaurant';
const ACTIVE_ROLE_KEY = 'restro360_active_role';

export function getActiveRestaurantId(): string | null {
  return localStorage.getItem(ACTIVE_RESTAURANT_KEY);
}

export function setActiveRestaurantId(id: string | null): void {
  if (id) localStorage.setItem(ACTIVE_RESTAURANT_KEY, id);
  else localStorage.removeItem(ACTIVE_RESTAURANT_KEY);
}

export function getActiveRole(): string | null {
  return localStorage.getItem(ACTIVE_ROLE_KEY);
}

export function setActiveRole(role: string | null): void {
  if (role) localStorage.setItem(ACTIVE_ROLE_KEY, role);
  else localStorage.removeItem(ACTIVE_ROLE_KEY);
}

/** Read config from localStorage cache (populated after login from backend). */
export function getPageAccessConfig(): PageAccessConfig {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored) as PageAccessConfig;
  } catch {
    // ignore
  }
  return { ...DEFAULT_CONFIG };
}

/** Cache config in localStorage (called after fetching from backend). */
export function savePageAccessConfig(config: PageAccessConfig): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
}

/** Decode JWT (no verification) and return the roles claim. */
export function getUserRolesFromToken(): string[] {
  try {
    const token = localStorage.getItem('accessToken');
    if (!token) return [];
    const payload = JSON.parse(atob(token.split('.')[1]));
    const roles = payload.roles;
    if (Array.isArray(roles)) return roles as string[];
    if (typeof roles === 'string') return [roles];
    return [];
  } catch {
    return [];
  }
}

export function canAccessPage(pageKey: string, userRoles: string[], config: PageAccessConfig): boolean {
  const allowed = config[pageKey];
  if (!allowed) return true; // unconfigured pages are open
  return userRoles.some(r => allowed.includes(r));
}
